import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TournamentsRepository, MatchesRepository } from '../services/repository';
import { showSnackbar } from './Snackbar.jsx';
import TournamentTypeFlag from './TournamentTypeFlag.jsx';
import { useConfirmation } from '../contexts/ConfirmationContext.jsx';
import { benchPointsForSittingOut } from '../utils/tournamentRanking.js';
import './TournamentDetails.css';

const TournamentDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { showConfirmation } = useConfirmation();
	const [tournament, setTournament] = useState(null);
	const [loading, setLoading] = useState(true);
	const [showPointsPopup, setShowPointsPopup] = useState(false);
	const [activeCell, setActiveCell] = useState(null);
	const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
	const [finishing, setFinishing] = useState(false);
	const [generatingRound, setGeneratingRound] = useState(false);
	const [newlyGeneratedRoundIndex, setNewlyGeneratedRoundIndex] = useState(null);
	
	useEffect(() => {
		if (id) {
			loadTournament();
		}
	}, [id]);
	
	useEffect(() => {
		// Add click outside listener when popup is shown
		if (showPointsPopup) {
			document.addEventListener('click', handleClickOutside);
			
			// Cleanup function
			return () => {
				document.removeEventListener('click', handleClickOutside);
				// Re-enable scroll when popup is closed
				document.body.style.overflow = '';
			};
		}
	}, [showPointsPopup]);
	
	const loadTournament = async () => {
		try {
			setLoading(true);
			const tournamentData = await TournamentsRepository.fetchTournament(id);
			setTournament(tournamentData);
		} catch (error) {
			console.error('Error loading tournament:', error);
			showSnackbar('Błąd ładowania turnieju');
		} finally {
			setLoading(false);
		}
	};
	
	const updateMatch = async (matchId, team1Result, team2Result) => {
		try {
			console.log('Updating match:', { matchId, team1Result, team2Result });
			const result = await MatchesRepository.updateMatch(matchId, team1Result, team2Result);
			console.log('Update match response:', result);
			
			// Update local state instead of reloading entire tournament
			setTournament(prevTournament => {
				if (!prevTournament) return prevTournament;
				
				const updatedTournament = { ...prevTournament };
				updatedTournament.rounds = updatedTournament.rounds.map(round => ({
					...round,
					matches: round.matches?.map(match => 
						match.id === matchId 
						? { ...match, team1Result, team2Result }
						: match
					)
				}));
				
				return updatedTournament;
			});
			
			showSnackbar('Wynik zaktualizowany');
		} catch (error) {
			console.error('Error updating match:', error);
			showSnackbar(`Błąd aktualizacji wyniku: ${error.message}`);
		}
	};
	
	const handlePointsCellClick = (matchId, side, currentValue, event) => {
		event.stopPropagation(); // Prevent event bubbling
		
		// Calculate center position
		const centerX = window.innerWidth / 2;
		const centerY = window.innerHeight / 2;
		
		setPopupPosition({
			x: centerX,
			y: centerY
		});
		setActiveCell({ matchId, side, currentValue });
		setShowPointsPopup(true);
		
		// Disable scroll
		document.body.style.overflow = 'hidden';
	};
	
	const handleClickOutside = (event) => {
		// Check if click is outside the popup
		const popup = document.querySelector('.points-popup');
		const clickedCell = event.target.closest('.points-cell');
		
		if (popup && !popup.contains(event.target) && !clickedCell) {
			setShowPointsPopup(false);
			setActiveCell(null);
			
			// Re-enable scroll
			document.body.style.overflow = '';
		}
	};
	
	const handlePopupNumberClick = (number) => {
		if (activeCell) {
			const { matchId, side } = activeCell;
			const match = findMatch(matchId);
			if (match) {
				const numberOfPoints = tournament.numberOfPoints || 21;
				const team1Result = side === 'team1' ? number : (numberOfPoints - number);
				const team2Result = side === 'team2' ? number : (numberOfPoints - number);
				updateMatch(matchId, team1Result, team2Result);
			}
		}
		setShowPointsPopup(false);
		setActiveCell(null);
		
		// Re-enable scroll
		document.body.style.overflow = '';
	};
	
	const handlePopupClick = (event) => {
		event.stopPropagation(); // Prevent closing when clicking inside popup
	};
	
	const findMatch = (matchId) => {
		for (const round of tournament.rounds) {
			const match = round.matches?.find(m => m.id === matchId);
			if (match) return match;
		}
		return null;
	};
	
	const isTournamentFinished = () => {
		if (!tournament) {
			return false;
		}
		
		// Check if the tournament has a finish date (set by the backend)
		return tournament.finishedAt !== null && tournament.finishedAt !== undefined;
	};
	
	const finishTournament = async () => {
		const confirmed = await showConfirmation({
			title: "Zakończ turniej",
			message: "Czy na pewno chcesz zakończyć ten turniej? Ta akcja jest nieodwracalna.",
			confirmText: "Zakończ",
			cancelText: "Anuluj",
			style: "destructive"
		});

		if (!confirmed) {
			return; // User cancelled
		}

		try {
			setFinishing(true);
			await TournamentsRepository.finishTournament(id);
			showSnackbar('Turniej zakończony pomyślnie');
			// Redirect to tournament summary after finishing
			navigate(`/summary/${id}`);
		} catch (error) {
			console.error('Error finishing tournament:', error);
			showSnackbar(`Błąd kończenia turnieju: ${error.message}`);
		} finally {
			setFinishing(false);
		}
	};
	
	const generateNextRound = async () => {
		try {
			setGeneratingRound(true);
			const newRound = await TournamentsRepository.generateNextRound(id);
			
				// Update local tournament state by adding the new round
			setTournament(prevTournament => {
				if (!prevTournament) return prevTournament;
				
				// Create a copy of the tournament with the new round
				const updatedTournament = { ...prevTournament };
				
				// The API returns a single round, so add it to the existing rounds
				if (newRound) {
					updatedTournament.rounds = [...(prevTournament.rounds || []), newRound];
				} else {
					console.warn('No round data received from generateNextRound');
					return prevTournament;
				}
				
				// Calculate the index of the newly generated round for highlighting
				const sortedRounds = [...updatedTournament.rounds].sort((a, b) => (a.order || 0) - (b.order || 0));
				const newRoundIndex = sortedRounds.length - 1; // Index of the last round
				
				// Set the newly generated round index for highlighting
				setNewlyGeneratedRoundIndex(newRoundIndex);
				
				// Scroll to the new round after a short delay to ensure DOM is updated
				setTimeout(() => {
					const roundElement = document.getElementById(`round-${newRoundIndex}`);
					if (roundElement) {
						roundElement.scrollIntoView({ 
							behavior: 'smooth', 
							block: 'center' 
						});
						
						// Add a brief flash effect
						roundElement.style.animation = 'newRoundFlash 0.5s ease-in-out';
						setTimeout(() => {
							roundElement.style.animation = '';
						}, 500);
					}
				}, 200);
				
				return updatedTournament;
			});
			
			showSnackbar('Nowa runda została wygenerowana pomyślnie');
			
			// Clear the highlight after 5 seconds
			setTimeout(() => {
				setNewlyGeneratedRoundIndex(null);
			}, 5000);
			
		} catch (error) {
			console.error('Error generating next round:', error);
			showSnackbar(`Błąd generowania nowej rundy: ${error.message}`);
		} finally {
			setGeneratingRound(false);
		}
	};
	
	const calculateRanking = () => {
		if (!tournament) return [];
		
		const playerPoints = {};

		tournament.participants?.forEach((tp) => {
			const label = tp.displayName || tp.participant?.name;
			if (label) playerPoints[label] = playerPoints[label] || 0;
		});
		
		tournament.rounds.forEach(round => {
			round.matches?.forEach(match => {
				const team1Players = [
					match.team1Participant1?.name,
					match.team1Participant2?.name
				].filter(Boolean);
				
				const team2Players = [
					match.team2Participant1?.name,
					match.team2Participant2?.name
				].filter(Boolean);
				
				const team1Points = match.team1Result || 0;
				const team2Points = match.team2Result || 0;
				
				team1Players.forEach(name => {
					playerPoints[name] = (playerPoints[name] || 0) + team1Points;
				});
				
				team2Players.forEach(name => {
					playerPoints[name] = (playerPoints[name] || 0) + team2Points;
				});
			});
		});

		if (tournament.type === 'americano') {
			const benchPts = benchPointsForSittingOut(tournament.numberOfPoints);
			if (benchPts > 0) {
				tournament.rounds?.forEach((round) => {
					if (!round.matches?.length) return;
					const allScored = round.matches.every(
						(m) => m.team1Result !== null && m.team2Result !== null
					);
					if (!allScored) return;
					const playingIds = new Set();
					round.matches.forEach((m) => {
						[m.team1Participant1, m.team1Participant2, m.team2Participant1, m.team2Participant2].forEach((p) => {
							if (p?.id) playingIds.add(p.id);
						});
					});
					tournament.participants?.forEach((tp) => {
						const p = tp.participant;
						if (!p?.id || playingIds.has(p.id)) return;
						const label = tp.displayName || p.name;
						if (label) playerPoints[label] = (playerPoints[label] || 0) + benchPts;
					});
				});
			}
		}
		
		return Object.entries(playerPoints)
		.map(([name, pts]) => ({ name, pts }))
		.sort((a, b) => b.pts - a.pts);
	};
	
	const renderMatch = (match) => {
		const team1Players = [
			match.team1Participant1?.name,
			match.team1Participant2?.name
		].filter(Boolean);
		
		const team2Players = [
			match.team2Participant1?.name,
			match.team2Participant2?.name
		].filter(Boolean);
		
		const maxRows = Math.max(team1Players.length, team2Players.length);
		
		return (
			<div className="table-responsive">
			<table key={match.id} className="table table-bordered table-hover" data-match-id={match.id}>
			<tbody>
			{Array.from({ length: maxRows }, (_, i) => (
				<tr key={i}>
				<td className="text-center align-middle" style={{ width: '35%' }}>
				{team1Players[i] || ''}
				</td>
				{i === 0 && (
					<td 
					className="text-center align-middle fw-bold bg-light cursor-pointer"
					rowSpan={maxRows}
					style={{ width: '15%', cursor: 'pointer' }}
					onClick={(e) => handlePointsCellClick(
						match.id, 
						'team1', 
						match.team1Result, 
						e
					)}
					>
					{match.team1Result !== null && match.team1Result !== undefined ? match.team1Result : ''}
					</td>
				)}
				{i === 0 && (
					<td 
					className="text-center align-middle fw-bold"
					rowSpan={maxRows}
					style={{ width: '5%', color: '#6c757d' }}
					>
					:
					</td>
				)}
				{i === 0 && (
					<td 
					className="text-center align-middle fw-bold bg-light cursor-pointer"
					rowSpan={maxRows}
					style={{ width: '15%', cursor: 'pointer' }}
					onClick={(e) => handlePointsCellClick(
						match.id, 
						'team2', 
						match.team2Result, 
						e
					)}
					>
					{match.team2Result !== null && match.team2Result !== undefined ? match.team2Result : ''}
					</td>
				)}
				<td className="text-center align-middle" style={{ width: '35%' }}>
				{team2Players[i] || ''}
				</td>
				</tr>
			))}
			</tbody>
			</table>
			</div>
		);
	};
	
	const getBenchPlayersForRound = (round) => {
		if (!tournament || tournament.type !== 'americano') return [];
		if (!round.matches?.length || !tournament.participants?.length) return [];
		const playingIds = new Set();
		round.matches.forEach((m) => {
			[m.team1Participant1, m.team1Participant2, m.team2Participant1, m.team2Participant2].forEach((p) => {
				if (p?.id) playingIds.add(p.id);
			});
		});
		return tournament.participants
			.filter((tp) => tp.participant?.id && !playingIds.has(tp.participant.id))
			.map((tp) => ({
				id: tp.participant.id,
				label: tp.displayName || tp.participant.name
			}))
			.filter((e) => e.label);
	};

	const renderRound = (round, index) => {
		const sortedMatches = [...(round.matches || [])].sort((a, b) => {
			const aNum = a.court?.courtNumber ?? 0;
			const bNum = b.court?.courtNumber ?? 0;
			return aNum - bNum;
		});
		
		const isNewlyGenerated = newlyGeneratedRoundIndex === index;
		const benchPlayers = getBenchPlayersForRound(round);
		
		// Split matches into 2 columns
		const leftColumnMatches = [];
		const rightColumnMatches = [];
		
		sortedMatches.forEach((match, matchIndex) => {
			if (matchIndex % 2 === 0) {
				leftColumnMatches.push(match);
			} else {
				rightColumnMatches.push(match);
			}
		});
		
		return (
			<div 
			key={index} 
			id={`round-${index}`}
			className={`mb-4 ${isNewlyGenerated ? 'new-round-highlight' : ''}`}
			style={{
				transition: 'all 0.3s ease-in-out',
				...(isNewlyGenerated && {
					backgroundColor: 'rgba(59, 130, 246, 0.15)',
					border: '3px solid #3b82f6',
					borderRadius: '12px',
					padding: '20px',
					boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
					position: 'relative'
				})
			}}
			>
			<div className="round-header mb-3">
			<h5 className="mb-0">
			Runda {index + 1}
			{isNewlyGenerated && (
				<span 
				className="badge ms-2"
				style={{
					backgroundColor: '#3b82f6',
					color: 'white',
					fontSize: '0.8rem',
					padding: '6px 12px',
					borderRadius: '20px',
					animation: 'newRoundPulse 2s ease-in-out infinite'
				}}
				>
				<i className="bi bi-star-fill me-1"></i>
				Nowa Runda!
				</span>
			)}
			</h5>
			{benchPlayers.length > 0 && (
				<div className="round-bench mt-2" role="status" aria-label="Uczestnicy na ławce w tej rundzie">
					<div className="round-bench-inner">
						<span className="round-bench-label">
							<i className="bi bi-person-dash me-1" aria-hidden="true" />
							Ławka w tej rundzie
						</span>
						<div className="round-bench-chips">
							{benchPlayers.map((p) => (
								<span key={p.id} className="round-bench-chip">
									{p.label}
								</span>
							))}
						</div>
					</div>
				</div>
			)}
			</div>
			<div className="round-content">
			{sortedMatches.length > 0 ? (
				<div className="round-columns">
				<div className="round-column">
				{leftColumnMatches.map(match => (
					<div key={match.id} className="match-item">
					<div className="text-muted small mb-2">
					<i className="bi bi-geo-alt"></i> {match.court?.name || 'Brak kortu'}
					</div>
					{renderMatch(match)}
					</div>
				))}
				</div>
				<div className="round-column">
				{rightColumnMatches.map(match => (
					<div key={match.id} className="match-item">
					<div className="text-muted small mb-2">
					<i className="bi bi-geo-alt"></i> {match.court?.name || 'Brak kortu'}
					</div>
					{renderMatch(match)}
					</div>
				))}
				</div>
				</div>
			) : (
				<div className="text-center text-muted py-3">
				<i className="bi bi-calendar-x fs-4"></i>
				<p className="mt-2 mb-0">Brak meczów</p>
				</div>
			)}
			</div>
			{isNewlyGenerated && (
				<div 
				style={{
					position: 'absolute',
					bottom: '-10px',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '60px',
					height: '4px',
					backgroundColor: '#3b82f6',
					borderRadius: '2px',
					animation: 'newRoundPulse 2s ease-in-out infinite'
				}}
				/>
			)}
			</div>
		);
	};
	
	const renderRanking = () => {
		const ranking = calculateRanking();
		
		if (ranking.length === 0) {
			return <div className="text-center text-muted py-3">
			<i className="bi bi-trophy fs-4"></i>
			<p className="mt-2 mb-0">Brak danych</p>
			</div>;
		}
		
		return (
			<div className="table-responsive">
			<table className="table table-bordered">
			<thead>
			<tr>
			<th style={{ width: '15%' }}>#</th>
			<th style={{ width: '60%' }}>Uczestnik</th>
			<th style={{ width: '25%', textAlign: 'center' }}>Punkty</th>
			</tr>
			</thead>
			<tbody>
			{ranking.map((player, index) => (
				<tr key={player.name} className={index < 3 ? 'fw-bold' : ''}>
				<td className={index === 0 ? 'text-warning' : index === 1 ? 'text-secondary' : index === 2 ? 'text-danger' : ''}>
				{index + 1}
				</td>
				<td className={index === 0 ? 'text-warning' : index === 1 ? 'text-secondary' : index === 2 ? 'text-danger' : ''}>
				{player.name}
				</td>
				<td 
				className={index === 0 ? 'text-warning' : index === 1 ? 'text-secondary' : index === 2 ? 'text-danger' : ''}
				style={{ textAlign: 'center' }}
				>
				{player.pts}
				</td>
				</tr>
			))}
			</tbody>
			</table>
			</div>
		);
	};
	
	const renderPointsPopup = () => {
		if (!showPointsPopup || !tournament) return null;
		
		const maxPoints = tournament.numberOfPoints || 21;
		const numbers = [0, ...Array.from({ length: maxPoints }, (_, i) => i + 1)];
		
		return (
			<>
			{/* Background overlay */}
			<div 
			className="points-overlay"
			onClick={handleClickOutside}
			/>
			
			{/* Points popup */}
			<div 
			className="points-popup"
			style={{
				left: popupPosition.x,
				top: popupPosition.y
			}}
			onClick={handlePopupClick}
			>
			<div className="card shadow border-0">
			<div className="card-body p-2">
			<div className="row g-1">
			{numbers.map(num => (
				<div key={num} className="col-3 col-sm-2 col-md-1">
				<button
				className="btn btn-outline-primary btn-sm w-100"
				onClick={() => handlePopupNumberClick(num)}
				style={{ minWidth: '40px', fontSize: '0.875rem' }}
				>
				{num}
				</button>
				</div>
			))}
			</div>
			</div>
			</div>
			</div>
			</>
		);
	};
	
	if (loading) {
		return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
		<div className="spinner-border text-primary" role="status">
		<span className="visually-hidden">Ładowanie turnieju...</span>
		</div>
		</div>;
	}
	
	if (!tournament) {
		return <div className="alert alert-danger text-center" role="alert">
		<i className="bi bi-exclamation-triangle fs-1"></i>
		<p className="mt-3 mb-0">Turniej nie został znaleziony</p>
		</div>;
	}
	
	return (
		<div className="container-fluid" style={{ maxWidth: '100%' }}>
		<div className="row">
		<div className="col-12">
		<div className="card shadow-sm mb-4">
		<div className="card-body">
		<div className="text-center mb-4">
		<h1 className="mb-0">
		<TournamentTypeFlag tournamentType={tournament.type} />
		{tournament.name}
		</h1>
		</div>
		<div className="row mb-4">
		<div className="col-12">
		<div className="tournament-info-card">
		<span className="tournament-info-text">
		<strong>Liczba punktów do wygranej:</strong> {tournament.numberOfPoints}
		</span>
		</div>
		</div>
		</div>
		
		<div className="rounds-container">
		{tournament.rounds
			?.sort((a, b) => (a.order || 0) - (b.order || 0))
			.map((round, index) => renderRound(round, index))}
			
			{/* Show buttons only for tournaments that are not finished */}
			{!isTournamentFinished() && (
				<div className="text-center mt-4">
				<div className="d-flex justify-content-center gap-3">
				<button
				className="btn btn-primary btn-lg"
				onClick={generateNextRound}
				disabled={generatingRound}
				>
				{generatingRound ? (
					<>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Generowanie...
					</>
				) : (
					'Nowa runda'
				)}
				</button>
				<button
				className="btn btn-danger btn-lg"
				onClick={finishTournament}
				disabled={finishing}
				>
				{finishing ? (
					<>
					<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
					Kończenie...
					</>
				) : (
					'Zakończ'
				)}
				</button>
				</div>
				</div>
			)}
			</div>
			</div>
			</div>
			</div>
			</div>
			
			<div className="row justify-content-start justify-content-lg-center">
			<div className="col-12 col-lg-8 col-xl-7">
			<div className="card shadow-sm">
			<div className="card-header">
			<h5 className="mb-0">Ranking</h5>
			</div>
			<div className="card-body">
			{renderRanking()}
			</div>
			</div>
			</div>
			</div>
			
			{renderPointsPopup()}
			</div>
		);
	};
	
	export default TournamentDetails; 